import axios, { AxiosInstance } from 'axios';
import { getAuthToken, getSessionExpiry, clearSession } from './auth.service';
import toast from 'react-hot-toast';
import { getAppContext, getFullReport, clearAppContext } from '../hooks/useAppContext';
import { setCompanyLocked } from './companyLock';
import { getConfig } from '../hooks/useConfig';
import {
  configureServerMonitor,
  isUnavailabilityError,
  reportServerReachable,
  reportServerUnreachable,
} from './serverMonitor';
import { clearPreferences } from './preferences';
import { invalidateDropdown } from './dropdownCache';

/** Parse JWT payload without a library — returns null if invalid/missing. */

/** Wipe all session keys and redirect to login. */
export function clearSessionAndRedirect(reason?: string): void {
  // The session cookie is HttpOnly, so the browser cannot delete it — only the server can, and
  // without this call a "log out" would leave a live credential behind. Fire-and-forget: the
  // redirect below must happen whether or not the API is reachable, which is often exactly why
  // the session is being cleared.
  try {
    void fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' });
  } catch { /* the local clear and the redirect still stand */ }
  clearSession();
  clearAppContext();
  clearPreferences();
  // App uses HashRouter — put the reason inside the hash so:
  //  (a) react-router's useSearchParams() can read it on /login, and
  //  (b) navigating away (e.g. to /dashboard after login) drops it automatically,
  //      instead of leaving ?reason=... stuck in the real URL forever.
  const hash = reason ? `#/login?reason=${reason}` : '#/login';
  window.location.replace(`${window.location.pathname}${hash}`);
  // A forced logout (API down, session expired, idle) must land on a FRESH login page: replacing
  // only the hash keeps the SPA's in-memory state (open modals, cached data, half-loaded screens)
  // alive behind the login form. A hard reload discards all of it; the reason survives in the
  // hash so Login can still explain what happened. The bundle is static, so this works even
  // while the API itself is unreachable.
  window.location.reload();
}

/**
 * True when a usable session token is stored. Not proof the session is valid — only the server can
 * say that — but enough to know whether a request is worth making at all.
 */
export function hasAuthToken(): boolean {
  // A signed-in identity plus an unexpired session. The credential itself is the cookie, which this
  // code cannot see — so "is there a token" is no longer the question that can be asked.
  return !!getSessionExpiry() && !isTokenExpired();
}

/**
 * True when the session has passed its expiry.
 *
 * Read from the stored expiry TIMESTAMP rather than by decoding the JWT, because the JWT is no
 * longer in storage to decode — it is in memory for this page only, and gone after a refresh while
 * the HttpOnly cookie carries on. The timestamp is not a credential and can be stored safely; the
 * server remains the judge of validity, and a session it rejects still comes back as a 401.
 */
export function isTokenExpired(): boolean {
  const expiry = getSessionExpiry();
  if (!expiry) return false;
  return Date.now() > expiry.getTime();
}

// On module load there is nothing to judge: the session is in an HttpOnly cookie this code
// cannot see, and it is established by asking the server (SessionBootstrap → /auth/me). Only
// the leftover ?reason=... in the real URL is tidied here.
(function checkOnLoad() {
  if (window.location.search.includes('reason=')) {
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
  }
})();

function resolveApiBaseUrl(): string {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    const url = envUrl.endsWith('/api') ? envUrl : envUrl.replace(/\/+$/, '') + '/api';
    return url;
  }
  // Production: if on *.electemb.com, use hrm-api.electemb.com
  const host = window.location.hostname.toLowerCase();
  if (host.endsWith('.electemb.com') || host === 'electemb.com') {
    const proto = window.location.protocol;
    return `${proto}//hrm-api.electemb.com/api`;
  }
  // Local development fallback — same host, port 5082
  const localBase = `http://${host}:5082/api`;
  return localBase;
}

export const API_BASE_URL = resolveApiBaseUrl();
export const API_BASE = API_BASE_URL.replace(/\/api$/, '');

// Wire the centralized availability monitor: probe the unauthenticated /ping endpoint and,
// after 5s of continuous failure, wipe the session and redirect to Login with a clear reason.
configureServerMonitor({
  pingUrl: `${API_BASE_URL}/ping`,
  onExpire: (reason) => clearSessionAndRedirect(reason),
});

/**
 * Cached reads that a write makes stale, keyed by the URL segment the write went to.
 *
 * Doing this in the interceptor rather than in each service method is deliberate. The employee
 * service alone has 37 mutating calls; annotating each one means the cache is correct only until
 * somebody adds the thirty-eighth and forgets — and a forgotten invalidation shows up as a roster
 * that silently refuses to include a new joiner, which is far worse than the request it saved.
 * There is exactly one place every write passes through, so the rule lives here and cannot be
 * bypassed by a new call site.
 *
 * Keys are prefixes into `dropdownCache`, whose entries are already scoped per company, unit and
 * mode, so clearing a prefix clears it for every scope — which is right, because the writer does
 * not know whose copy is stale.
 */
const WRITE_INVALIDATES: ReadonlyArray<readonly [RegExp, readonly string[]]> = [
  [/\/employees?\b/i,     ['employees:']],
  [/\/shifts?\b/i,        ['shifts:', 'shift-rotations:']],
  [/\/departments?\b/i,   ['dept:']],
  [/\/designations?\b/i,  ['desig:']],
  [/\/categor(y|ies)\b/i, ['cat:']],
  [/\/grades?\b/i,        ['grade:']],
  [/\/units?\b/i,         ['unit:']],
  [/\/holidays?\b/i,      ['holiday:']],
];

function invalidateCacheForWrite(method?: string, url?: string): void {
  const verb = (method ?? 'get').toLowerCase();
  if (verb === 'get' || verb === 'head' || verb === 'options') return;
  if (!url) return;
  for (const [pattern, prefixes] of WRITE_INVALIDATES) {
    if (pattern.test(url)) prefixes.forEach(invalidateDropdown);
  }
}

class ApiClient {
  private client: AxiosInstance;
  private _redirecting = false; // guard against multiple parallel redirects

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      // Send the HttpOnly session cookie. The server issues it at login and accepts it when no
      // Authorization header arrives, so the two credentials coexist: this alone changes nothing
      // for a client still sending the header, and is what lets one stop.
      withCredentials: true,
    });

    // Request interceptor to add auth token + app context headers
    this.client.interceptors.request.use(
      (config) => {
        // Reports page Custom Range filter: scope /report endpoints to the selected range.
        if (reportRange && (config.url || '').includes('/report')) {
          config.headers['X-Report-Range'] = reportRange;
        }
        // Reports page Payroll Period filter: build every payroll document from ONE payroll run
        // (a week of a Weekly category) instead of the consolidated month.
        if (payrollRunScope && (config.url || '').includes('/report')) {
          config.headers['X-Payroll-Run-Id'] = String(payrollRunScope);
        }
        const token = getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          // Count user-initiated API interactions as activity so the idle timer resets.
          // Only mutations (POST/PUT/PATCH/DELETE) — GETs include background pollers
          // (license/lock checks) that must NOT keep an otherwise-idle session alive.
          const method = (config.method || 'get').toLowerCase();
          if (method !== 'get' && method !== 'head' && method !== 'options') {
            try { window.dispatchEvent(new Event('app:activity')); } catch { /* no window */ }
          }
        }

        // Let axios auto-set Content-Type (with boundary) for FormData
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        // Inject company / branch / financial-year context
        const ctx = getAppContext();
        if (ctx.companyId) config.headers['X-Company-Id'] = String(ctx.companyId);
        if (ctx.branchId) config.headers['X-Branch-Id'] = String(ctx.branchId);
        if (ctx.financialYearId) config.headers['X-Financial-Year-Id'] = String(ctx.financialYearId);
        // Full Report goes as BOTH a header and a query parameter, deliberately.
        //
        // The header alone decided who appeared on a statutory register, and anything between the
        // browser and the server may drop an unrecognised header — a proxy, a CDN, an appliance.
        // When that happens the register silently comes back trimmed: employees who were absent all
        // month are missing, with no error to show for it. The header is also absent from access
        // logs, so afterwards nobody can tell whether the flag ever arrived.
        //
        // The query parameter survives proxies and appears in the server's own request log, which
        // turns "was Full Report on?" into something answerable. Only report requests carry it, so
        // the rest of the API is unaffected. The backend takes the query value first and falls back
        // to the header, so an older client that sends only the header still works.
        if (getFullReport()) {
          config.headers['X-Full-Report'] = 'true';
          if ((config.url ?? '').includes('report')) {
            config.params = { ...(config.params ?? {}), fullReport: 'true' };
          }
        }

        // The Date Format chosen in General Settings, sent with anything the server renders dates
        // into. The server formats reports itself, so without this the setting would apply to the
        // screens and stop at the PDF — the same date shown two ways in one session. Sent as a
        // parameter rather than looked up server-side: the client already knows what the user
        // picked, it costs no database read, and it appears in the request log. The server falls
        // back to the company's stored setting when it is absent, which is what background jobs
        // with no caller rely on.
        const df = getConfig().dateFormat;
        if (df && /report|payslip|export|muster|register/i.test(config.url ?? '')) {
          config.params = { ...(config.params ?? {}), dateFormat: df };
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: central network monitor + auth/lock handling.
    this.client.interceptors.response.use(
      (response) => {
        reportServerReachable(); // any successful response proves the backend is alive
        invalidateCacheForWrite(response.config?.method, response.config?.url);
        return response;
      },
      (error) => {
        if (this._redirecting) return Promise.reject(error);

        const status = error.response?.status;
        // "Did this request belong to a session we believed in?" A bearer header says so; so does
        // an unexpired session in memory whose credential rode along as the cookie. Without the
        // second half, a 401 on a cookie-only request — every request after a reload — would never
        // have been read as the session ending.
        const hadToken = !!error.config?.headers?.Authorization || hasAuthToken();

        if (isUnavailabilityError(error)) {
          // Backend unreachable (stopped/terminated/network down/502/503/504/timeout).
          // Feed the centralized monitor rather than logging out immediately: it waits for
          // 5s of *continuous* failure (with a recovery probe) before wiping the session, so
          // a transient blip or a slow report does not eject the user. Only monitor requests
          // that carried a token — public/login-page calls must never start the logout clock.
          if (hadToken) reportServerUnreachable();
          return Promise.reject(error);
        }

        // The server responded (any HTTP status) → it is reachable; clear any degraded state.
        reportServerReachable();

        if (status === 423) {
          setCompanyLocked(true);
          const msg = error.response?.data?.message ??
            'Company is locked by Platform Administrator. HRMS is in Read-Only Mode.';
          toast.error(msg, { id: 'company-locked', duration: 6000 });
        } else if (status === 401 && hadToken) {
          // Only treat as session expiry if the request actually carried a token.
          // A 401 on a public endpoint (e.g. /companies/public-list, called from the
          // login page with no token) must not trigger a "session expired" redirect.
          this._redirecting = true;
          clearSessionAndRedirect('session_expired');
        }
        return Promise.reject(error);
      }
    );
  }

  get<T>(url: string, config = {}) {
    return this.client.get<T>(url, config);
  }

  post<T>(url: string, data?: any, config = {}) {
    return this.client.post<T>(url, data, config);
  }

  put<T>(url: string, data?: any, config = {}) {
    return this.client.put<T>(url, data, config);
  }

  patch<T>(url: string, data?: any, config = {}) {
    return this.client.patch<T>(url, data, config);
  }

  delete<T>(url: string, config = {}) {
    return this.client.delete<T>(url, config);
  }
}

/** Module-level report-range override ("yyyy-MM-dd..yyyy-MM-dd") attached to /report requests. */
let reportRange: string | null = null;
export function setReportRange(from?: string | null, to?: string | null): void {
  reportRange = from && to ? `${from}..${to}` : null;
}

/**
 * Module-level payroll-run scope attached to /report requests as X-Payroll-Run-Id. Null = the
 * whole month (weekly runs consolidated server-side). An explicit `payrollRunId` query value on a
 * single request wins over this header.
 */
let payrollRunScope: number | null = null;
export function setPayrollRunScope(runId?: number | null): void {
  payrollRunScope = runId && runId > 0 ? runId : null;
}

export const apiClient = new ApiClient();
